import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus, faBuilding } from '@fortawesome/free-solid-svg-icons';
import RequestPropertyModal from '../ClientRequests/RequestPropertyModal';
import EmptyState from '../EmptyState';
import useClientRequestsEnabled from '../ClientRequests/useClientRequestsEnabled';
import "./clientProperties.css";

function ClientProperties() {
  const [properties, setProperties] = useState([]);
  // Nothing is decided until BOTH loads are in: an empty list while the
  // request is still in flight is not "no properties yet" (owner, 2026-09-14).
  const [loading, setLoading] = useState(true);
  // Pending create_property requests (2026-08-04): a requested property
  // must be VISIBLE here, marked pending — an empty page over a submitted
  // request reads as "it didn't go through".
  const [pendingRequests, setPendingRequests] = useState([]);
  // Draft properties (2026-09-14): the 4-step add-property form saves a draft
  // after step 1, and the client continues it from this page.
  const [drafts, setDrafts] = useState([]);
  const [activeDraft, setActiveDraft] = useState(null);
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  // The deep-link effects run before openNew is defined below.
  const openNewRef = React.useRef(() => {});

  // Opened from the Home page quick action (/client/properties?new=1) so the
  // "Add a property" button reuses THIS modal instead of duplicating the form.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('new') === '1') {
      openNewRef.current();
      // drop the param so a refresh/back does not re-open it
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  const requestsEnabled = useClientRequestsEnabled();
  const navigate = useNavigate();

  const fetchRequests = useCallback(async () => {
    try {
      const [pendingRes, draftRes] = await Promise.all([
        axios.get('/api/client-requests/requests/my', { params: { status: 'pending', type: 'create_property' } }),
        axios.get('/api/client-requests/requests/my', { params: { status: 'draft', type: 'create_property' } }),
      ]);
      setPendingRequests(Array.isArray(pendingRes.data?.requests) ? pendingRes.data.requests : []);
      setDrafts(Array.isArray(draftRes.data?.requests) ? draftRes.data.requests : []);
    } catch (e) {
      // feature off / error → no pending or draft rows, properties still render
      setPendingRequests([]);
      setDrafts([]);
    }
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get('/api/client-requests/properties');
        const sortedProperties = response.data.properties.sort((a, b) => a.property_id - b.property_id);
        setProperties(sortedProperties);
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
      await fetchRequests();
      setLoading(false);
    };

    fetchProperties();
  }, [fetchRequests]);

  const openNew = () => { setActiveDraft(null); setIsRequestModalOpen(true); };
  openNewRef.current = openNew;
  const openDraft = (r) => { setActiveDraft(r); setIsRequestModalOpen(true); };
  const closeModal = () => { setIsRequestModalOpen(false); setActiveDraft(null); fetchRequests(); };


  const formatPropertyId = (id) => `#${id.toString().padStart(5, '0')}`;
  const formatBedrooms = (bedrooms) => bedrooms === 0 ? 'S' : bedrooms;

  const filteredProperties = Array.isArray(properties)
    ? properties.filter(property => {
      const buildingName = property.building_name || '';
      const unitNumber = property.unit_number || '';
      const propertyIdFormatted = formatPropertyId(property.property_id);
      const propertyId = property.property_id ? property.property_id.toString() : '';

      return (
        buildingName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        unitNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        propertyId.includes(searchTerm) ||
        propertyIdFormatted.includes(`#${searchTerm.padStart(5, '0')}`)
      );
    })
    : [];

  // Deep entry (?add=1) from "add your first property" CTAs opens the modal
  // immediately — the person already said what they want to do.
  useEffect(() => {
    if (searchParams.get('add') === '1') openNewRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRowClick = (propertyId) => {
    navigate(`/client/properties/${propertyId}`);
  };

  return (
    <div className="properties-container clProps">
      <div className='clPropHeader'>
        <div className="cb__headerLeft">
          <h2 className='cb__title'>Your Properties</h2>
          <div className="cb__sub">Search between your properties</div>
        </div>
        <div className="client-properties-search-container">
          <div className="search-input-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search by building, unit number, or property id..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="client-properties-search-bar"
            />
          </div>
          {requestsEnabled && (
            <button
              className="client-request-property-btn"
              onClick={openNew}
            >
              <FontAwesomeIcon icon={faPlus} /> Add New Property
            </button>
          )}
        </div>

      </div>

      {loading ? (
        <div className="clProps__tableCard clProps__loading" aria-busy="true">
          <div className="clProps__skelRow" />
          <div className="clProps__skelRow" />
          <div className="clProps__skelRow" />
        </div>
      ) : properties.length === 0 && pendingRequests.length === 0 && drafts.length === 0 ? (
        /* Fresh account (2026-08-04): a bare table header is not an
           invitation. Same EmptyState family as the rest of the client app,
           with the CTA straight into the add-property flow. */
        <div className="clProps__tableCard">
          <EmptyState
            icon={faBuilding}
            title="No properties yet"
            text="Add your first property and you can start booking cleanings for it right away."
          >
            {requestsEnabled && (
              <button
                className="client-request-property-btn"
                style={{ marginTop: 14 }}
                onClick={openNew}
              >
                <FontAwesomeIcon icon={faPlus} /> Add your first property
              </button>
            )}
          </EmptyState>
        </div>
      ) : (
      <div className="clProps__tableCard">
        <table className="properties-table">
        <thead>
          <tr>
            <th>Property ID</th>
            <th>Building Name</th>
            <th style={{ textAlign: 'center' }}>Unit Number</th>
            <th style={{ textAlign: 'center' }}>Bedrooms</th>
            <th style={{ textAlign: 'center' }}>Area</th>
          </tr>
        </thead>
        <tbody>
          {drafts.map((r) => {
            const p = r.payload || {};
            const stepDone = Math.min(3, Number(p.draft_step) || 1);
            return (
              <tr
                key={`draft-${r.id}`}
                className="property-row property-row--draft"
                onClick={() => openDraft(r)}
                style={{ cursor: 'pointer' }}
                title="Continue adding this property"
              >
                <td><span className="clProps__draftChip">Draft</span></td>
                <td>{p.address_line_1 || '—'}</td>
                <td style={{ textAlign: 'center' }}>{p.unit_number || '—'}</td>
                <td style={{ textAlign: 'center' }}>{stepDone >= 2 ? formatBedrooms(Number(p.bedrooms) || 0) : '—'}</td>
                <td style={{ textAlign: 'center' }}>{p.area || '—'}</td>
              </tr>
            );
          })}
          {pendingRequests.map((r) => {
            const p = r.payload || {};
            return (
              <tr key={`pending-${r.id}`} className="property-row property-row--pending">
                <td><span className="clProps__pendingChip">Pending review</span></td>
                <td>{p.address_line_1 || '—'}</td>
                <td style={{ textAlign: 'center' }}>{p.unit_number || '—'}</td>
                <td style={{ textAlign: 'center' }}>{formatBedrooms(Number(p.bedrooms) || 0)}</td>
                <td style={{ textAlign: 'center' }}>{p.area || '—'}</td>
              </tr>
            );
          })}
          {filteredProperties.map(property => (
            <tr
              key={property.property_id}
              onClick={() => handleRowClick(property.property_id)}
              className="property-row"
              style={{ cursor: 'pointer' }}
            >
              <td>{formatPropertyId(property.property_id)}</td>
              <td>{property.building_name}</td>
              <td style={{ textAlign: 'center' }}>{property.unit_number}</td>
              <td style={{ textAlign: 'center' }}>{formatBedrooms(property.bedrooms)}</td>
              <td style={{ textAlign: 'center' }}>{property.area}</td>
            </tr>
          ))}
        </tbody>
      </table>
        </div>
      )}

      {isRequestModalOpen && (
        <RequestPropertyModal
          key={activeDraft ? `draft-${activeDraft.id}` : 'new'}
          draft={activeDraft}
          onClose={closeModal}
          onSaved={() => fetchRequests()}
          onSubmitted={() => fetchRequests()}
        />
      )}
    </div>
  );
}

export default ClientProperties;